import { AsyncPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { SvgComponent } from '@app/base-components';
import { AppliedPromotionsDisclaimerComponent } from '@app/cart';
import { HabaQaId } from '@app/helpers';
import { CheckoutDataService, UserService } from '@app/services';
import {
  ActiveCartService,
  Address,
  CartActions,
  GlobalMessageService,
  GlobalMessageType,
  I18nModule,
  NewCustomerType,
  RoutingService,
  StateUtils,
  TranslationService,
  User,
  UserAddressService,
  UserCostCenterService,
  WindowRef,
} from 'haba-core';
import { EmporixStockService } from 'haba-emporix';
import { CheckoutStepService, ReviewSubmitComponent } from 'haba-featurelibs/checkout/components';
import { CheckoutDeliveryFacade, CheckoutPaymentFacade } from 'haba-featurelibs/checkout/root';
import { ModalService } from 'haba-storefrontlib';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomerType } from 'src/app/@enums/customerType';
import { HabaPaymentDetails } from '../../@interfaces/PaymentDetails';
import { ViewAddressComponent } from '../../cart/cart-shared/view-address/view-address.component';
import { CheckoutHeadlineComponent } from '../checkout-headline/checkout-headline.component';
import { CheckoutProductsComponent } from '../checkout-products/checkout-products.component';
import { EditDataModalComponent, ModalType } from '../edit-data-modal';
import { PendingRegistrationNoticeComponent } from '../pending-registration-notice';
import { Actions, ofType } from '@ngrx/effects';
import { CheckoutActions } from 'haba-featurelibs/checkout/core';

@Component({
  selector: 'app-review-submit',
  templateUrl: './review-submit.component.html',
  styleUrls: ['./review-submit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CheckoutHeadlineComponent,
    SvgComponent,
    NgIf,
    ViewAddressComponent,
    I18nModule,
    CheckoutProductsComponent,
    AsyncPipe,
    AppliedPromotionsDisclaimerComponent,
    PendingRegistrationNoticeComponent,
  ],
})
export class HabaReviewSubmitComponent extends ReviewSubmitComponent implements OnInit, OnDestroy {
  baseQaId: HabaQaId;
  habaQa: { billingAddress: string; deliveryAddress: string; paymentMethod: string } | null = null;
  billingAddress: Address;
  deliveryAddress: Address;
  paymentDetails: HabaPaymentDetails;
  paymentDetailsImage = signal<string>('');
  private onDestroy$ = new Subject();
  companyBillingAddress: Address;
  /** the chosen deviating invoice email: the cart mixin first, the old field after */
  invoiceEmail: string | null = null;

  NewCustomerType = NewCustomerType;
  CustomerType = CustomerType;
  readonly showDeliveryContact = false;

  user$: Observable<User> = this.userService.getCurrentUser();
  isGuestCart = this.activeCartService.isGuestCart();

  constructor(
    checkoutDeliveryService: CheckoutDeliveryFacade,
    checkoutPaymentService: CheckoutPaymentFacade,
    userAddressService: UserAddressService,
    activeCartService: ActiveCartService,
    translation: TranslationService,
    checkoutStepService: CheckoutStepService,
    userCostCenterService: UserCostCenterService,
    public checkoutDataService: CheckoutDataService,
    private changeRef: ChangeDetectorRef,
    private modalService: ModalService,
    private messagesService: GlobalMessageService,
    private routingService: RoutingService,
    private winRef: WindowRef,
    private emporixStockService: EmporixStockService,
    private userService: UserService,
    private actions$: Actions
  ) {
    super(
      checkoutDeliveryService,
      checkoutPaymentService,
      userAddressService,
      activeCartService,
      translation,
      checkoutStepService,
      userCostCenterService
    );
  }

  ngOnInit(): void {
    this.initIds();
    this.checkoutDataService.refreshCheckoutData();
    this.checkoutDataService.checkoutData$.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
      const { deliveryAddress, paymentAddress, paymentDetails, companyBillingAddress } = data || {};
      this.billingAddress = paymentAddress;
      this.deliveryAddress = deliveryAddress;
      this.paymentDetails = paymentDetails;
      this.paymentDetailsImage.set(this.getPaymentMethodImage(paymentDetails));
      this.companyBillingAddress = companyBillingAddress;
      this.invoiceEmail =
        data?.invoiceAddress?.email ?? companyBillingAddress?.invoiceEmailAddress ?? null;
      this.changeRef.detectChanges();
    });

    this.actions$
      .pipe(ofType(CheckoutActions.SET_PAYMENT_DETAILS_SUCCESS), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.checkoutDataService.refreshCheckoutData();
      });

    this.actions$
      .pipe(ofType(CartActions.LOAD_CART_SUCCESS), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.checkoutDataService.refreshCheckoutData();
      });

    this.checkoutPaymentService
      .getSetPaymentDetailsResultProcess()
      .subscribe((state: StateUtils.LoaderState<never>) => {
        if (
          !state.loading &&
          state.error &&
          !this.modalService.getActiveModal() &&
          !this.winRef.location.href.includes('/payment-details') &&
          this.paymentDetails?.id
        ) {
          this.checkoutPaymentService.resetSetPaymentDetailsProcess();
          this.messagesService.add(
            { key: 'habaPayment.disabledPaymentMethods.' + this.paymentDetails.id },
            GlobalMessageType.MSG_TYPE_ERROR
          );
          this.routingService.go({ cxRoute: 'paymentDetails' });
        }
      });

    setTimeout(() => {
      this.emporixStockService.checkStock();
    }, 500);
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  getPaymentMethodImage(paymentDetails: HabaPaymentDetails): string {
    if (!paymentDetails) return '';
    return `/assets/images/logos/${paymentDetails.code}.webp`;
  }

  public editModal(type: ModalType): void {
    const modalInstance = this.modalService.open(EditDataModalComponent, {
      beforeDismiss: () => modalInstance.componentInstance.shouldDismiss(type),
    });
    modalInstance.componentInstance.type = type;
  }

  private initIds() {
    this.baseQaId = HabaQaId.forPage(HabaQaId.page.reviewOrder);
    this.habaQa = {
      billingAddress: this.baseQaId
        .getButtonId(HabaQaId.elements.button.billingAddress)
        .getButtonId(),
      deliveryAddress: this.baseQaId
        .getButtonId(HabaQaId.elements.button.deliveryAddress)
        .getButtonId(),
      paymentMethod: this.baseQaId
        .getButtonId(HabaQaId.elements.button.paymentMethod)
        .getButtonId(),
    };
  }
}
